import { Focusable } from './Focusable.tsx';

/**
 * A keyboard drawn on the screen, because a television does not have one.
 *
 * WHY THIS EXISTS RATHER THAN AN <input>.
 *
 * A WebView on Android TV does not reliably raise the system IME when a text
 * field takes focus — on a Fire TV the search screen came up with a cursor
 * blinking in a box and no way on earth to type into it. That is not a bug to
 * be worked around with a focus() call; the platform genuinely does not
 * consider a D-pad an input method for text, and it is why every television
 * application draws its own keys.
 *
 * ALPHABETICAL, NOT QWERTY, and that is deliberate. QWERTY is muscle memory
 * for ten fingers on a physical board; with one cursor moving a square at a
 * time it is a scramble you have to read letter by letter. Alphabetical order
 * means you know which direction to travel before you start moving, which is
 * the only thing that makes a D-pad keyboard bearable.
 *
 * Six columns because the rows then stay square-ish on a 16:9 screen, and
 * because a cursor crossing a long thin row feels slower than one crossing a
 * block of the same size.
 */
const KEYS = [
  'ABCDEF',
  'GHIJKL',
  'MNOPQR',
  'STUVWX',
  'YZ0123',
  '456789',
];

export function Keyboard({
  onKey, onDelete, onClear, onDone,
}: {
  onKey(ch: string): void;
  onDelete(): void;
  onClear(): void;
  onDone?(): void;
}) {
  return (
    <div className="kb">
      {KEYS.map((row, r) => (
        <div className="kb-row" key={row}>
          {[...row].map((ch) => (
            <Focusable
              key={ch}
              id={`kb-${ch}`}
              className="btn kb-key"
              /* The first key takes focus on arrival: there is exactly one
                 thing to do on this screen, and making somebody travel to the
                 keyboard first is a press spent on nothing. */
              autoFocus={r === 0 && ch === 'A'}
              onSelect={() => onKey(ch)}
            >
              {ch}
            </Focusable>
          ))}
        </div>
      ))}

      <div className="kb-row">
        {/* Space is wide because it is pressed most and missing it costs a
            whole word; delete sits next to it because the two are used in the
            same breath. */}
        <Focusable id="kb-space" className="btn kb-key kb-wide" onSelect={() => onKey(' ')}>
          space
        </Focusable>
        <Focusable id="kb-del" className="btn kb-key kb-wide" onSelect={onDelete}>
          delete
        </Focusable>
      </div>

      <div className="kb-row">
        <Focusable id="kb-clear" className="btn kb-key kb-wide" onSelect={onClear}>
          clear
        </Focusable>
        {onDone && (
          <Focusable id="kb-done" className="btn kb-key kb-wide" onSelect={onDone}>
            done
          </Focusable>
        )}
      </div>
    </div>
  );
}
